import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Save, X, FileText, User, MapPin,
  Calendar, Hash, DollarSign, Percent, Calculator,
  Building, Phone, Mail, Search, ChevronDown, CheckCircle, RefreshCw, Plus, Trash2,
  HelpCircle
} from 'lucide-react';
import Breadcrumb from '../../components/Common/Breadcrumb';
import HelpModal from '../../components/HelpModal/HelpModal';
import CustomerSelectionModal from '../../components/CustomerSelectionModal/CustomerSelectionModal';
import ServiceSelectionTable from '../../components/ServiceSelectionTable/ServiceSelectionTable';
import ConfirmationModal from '../../components/ConfirmationModal/ConfirmationModal';
import { toast } from 'react-toastify';
import companyService from '../../services/companyService';
import proformaInvoiceService from '../../services/proformaInvoiceService';
import paymentTermsMasterService from '../../services/paymentTermsMasterService';
import projectService from '../../services/projectService';
import { calculateInvoiceGST } from '../../utils/gstCalculator';

const ProformaInvoiceForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = id && id !== 'new';
  const isView = window.location.pathname.includes('/view/');
  const isApprove = window.location.pathname.includes('/approve/');

  // Default Terms & Conditions for Proforma Invoice
  const DEFAULT_TERMS_AND_CONDITIONS = [
    'This is a Proforma Invoice only and not a Tax/GST Invoice.',
    'No GST input credit can be claimed on this document.',
    'Taxes will be charged as per prevailing GST law in the final invoice.',
    'A final Tax Invoice will be issued upon receipt of PO/confirmation/advance payment.'
  ];

  const [activeTab, setActiveTab] = useState('invoiceDetails');
  const [showHelp, setShowHelp] = useState(false);
  const [formData, setFormData] = useState({
    // Invoice Details
    proformaInvoiceNumber: '',
    proformaInvoiceDate: new Date().toISOString().split('T')[0],
    validUntil: '',
    poNumber: '',
    poDate: '',
    
    // Customer Details
    customerId: '',
    customerName: '',
    customerCode: '',
    customerCategory: '',
    customerAddress: '',
    customerCity: '',
    customerState: '',
    customerPincode: '',
    customerPhone: '',
    customerEmail: '',
    customerGSTIN: '',
    customerPaymentTerms: '',
    currency: '',
    
    // Billing Details
    billingAddress: '',
    billingCity: '',
    billingState: '',
    billingPincode: '',
    placeOfSupply: '',
    items: [],
    
    // GST Details
    cgstRate: 9,
    sgstRate: 9,
    igstRate: 18,
    cgstAmount: 0,
    sgstAmount: 0,
    utgstAmount: 0,
    igstAmount: 0,
    totalTaxAmount: 0,
    subtotal: 0,
    totalAmount: 0,
    gstType: 'IGST',
    isLUTBond: false,
    
    // Additional Details
    notes: '',
    terms: [],
    status: 'Draft',
    remark: '',
    totalValue: 0.00,
    
    // Project Details
    projectName: '',
    projectId: '',
    referenceModel: 'Customer'
  });

  const [loading, setLoading] = useState(false);
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [serviceData, setServiceData] = useState([]);
  const [showServiceTable, setShowServiceTable] = useState(false);
  const [companyGSTIN, setCompanyGSTIN] = useState('');
  const [companyState, setCompanyState] = useState('');
  const [customerGSTIN, setCustomerGSTIN] = useState('');
  const [paymentTermsOptions, setPaymentTermsOptions] = useState([]);
  const [projectOptions, setProjectOptions] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showConvertModal, setShowConvertModal] = useState(false);
  const serviceTableRef = useRef(null);
  const [canCreateInvoice, setCanCreateInvoice] = useState(false);
  const [newTerm, setNewTerm] = useState('');

  // Load company GSTIN and payment terms on component mount
  useEffect(() => {
    const fetchCompanyGSTIN = async () => {
      try {
        const response = await companyService.getCompanyDetails();
        if (response && response.success && response.data) {
          if (response.data.GSTIN) setCompanyGSTIN(response.data.GSTIN);
          if (response.data.companyBillingAddress?.state) {
            setCompanyState(response.data.companyBillingAddress.state);
          }
        }
      } catch (error) {
        console.error('Error fetching company GSTIN:', error);
        const storedGSTIN = localStorage.getItem('companyGSTIN');
        if (storedGSTIN) {
          setCompanyGSTIN(storedGSTIN);
        } else {
          setCompanyGSTIN('27AAGCI0729M1ZB');
        }
      }
    };

    const fetchPaymentTerms = async () => {
      try {
        const response = await paymentTermsMasterService.getActivePaymentTerms();
        if (response && response.success) {
          setPaymentTermsOptions(response.data || []);
        }
      } catch (error) {
        console.error('Error fetching payment terms:', error);
      }
    };

    fetchCompanyGSTIN();
    fetchPaymentTerms();
  }, [navigate]);

  // Set default valid until date (30 days from invoice date) for backend
  const getDefaultValidUntil = (invoiceDate) => {
    if (!invoiceDate) return null;
    const date = new Date(invoiceDate);
    date.setDate(date.getDate() + 30);
    return date.toISOString().split('T')[0];
  };

  // Initialize default terms and validUntil for new proforma invoices
  useEffect(() => {
    if ((!id || id === 'new') && (!formData.terms || formData.terms.length === 0)) {
      const invoiceDate = new Date(formData.proformaInvoiceDate);
      const validUntilDate = new Date(invoiceDate);
      validUntilDate.setDate(validUntilDate.getDate() + 30);
      
      setFormData(prev => ({
        ...prev,
        terms: [...DEFAULT_TERMS_AND_CONDITIONS],
        validUntil: validUntilDate.toISOString().split('T')[0]
      }));
    }
  }, [id]);

  // Load data for edit/view/approve mode
  useEffect(() => {
    if (isEdit || isView || isApprove) {
      loadInvoiceData();
    }
  }, [id, isEdit, isView, isApprove]);

  // Trigger GST calculation when switching to GST Details tab or when GSTINs change
  useEffect(() => {
    if (activeTab === 'gstDetails' && formData.totalValue > 0) {
      calculateGST(formData.totalValue);
    }
  }, [activeTab, formData.totalValue, companyGSTIN, customerGSTIN, formData.placeOfSupply, formData.customerCategory, companyState, formData.isLUTBond]);

  const loadInvoiceData = async () => {
    setLoading(true);
    try {
      if (id && id !== 'new') {
        const response = await proformaInvoiceService.getProformaInvoiceById(id);
        if (response && response.success && response.data) {
          const invoice = response.data;
          
          const invoiceDate = invoice.proformaInvoiceDate 
            ? new Date(invoice.proformaInvoiceDate).toISOString().split('T')[0] 
            : new Date().toISOString().split('T')[0];
          const validUntilDate = invoice.validUntil 
            ? new Date(invoice.validUntil).toISOString().split('T')[0] 
            : '';
          const poDate = invoice.PODate 
            ? new Date(invoice.PODate).toISOString().split('T')[0] 
            : '';
          
          const currency = invoice.currency || 
                          (invoice.customer && invoice.customer.customerCurrency) || 
                          '';
          
          let cgstRate = 9;
          let sgstRate = 9;
          let igstRate = 18;
          
          if (invoice.serviceDetails && invoice.serviceDetails.length > 0) {
            const firstService = invoice.serviceDetails[0];
            cgstRate = firstService.cgstRate || 9;
            sgstRate = firstService.sgstRate || 9;
            igstRate = firstService.igstRate || 18;
          }
          
          setFormData({
            proformaInvoiceNumber: invoice.proformaInvoiceNumber || '',
            proformaInvoiceDate: invoiceDate,
            validUntil: validUntilDate,
            poNumber: invoice.PONo || '',
            poDate: poDate,
            customerId: invoice.customer?._id || invoice.customer || '',
            customerName: invoice.customerName || '',
            customerCode: invoice.customerCode || (invoice.customer?.customerCode || ''),
            customerCategory: invoice.customerCategory || 'Domestic',
            customerGSTIN: invoice.customerGSTIN || (invoice.customer?.GSTIN || ''),
            customerPaymentTerms: invoice.customerPaymentTerms || '',
            currency: currency,
            projectName: invoice.projectName || '',
            projectId: invoice.project?._id || invoice.project || '',
            referenceModel: invoice.referenceModel || 'Customer',
            items: invoice.serviceDetails?.map((service, index) => ({
              id: service._id || service.service?._id || index + 1,
              serviceId: service.service?._id || service.service || '',
              itemCode: service.serviceCode || '',
              itemName: service.serviceDescription || '',
              serviceDescription: service.serviceDescription || '',
              description: service.serviceDescription || '',
              quantity: service.qty || 0,
              unit: 'Nos',
              rate: service.unitRate || 0,
              amount: service.lineValue || 0,
              discount: 0,
              taxableAmount: service.lineValue || 0,
              lineValue: service.lineValue || 0,
              cgstRate: service.cgstRate || cgstRate,
              sgstRate: service.sgstRate || sgstRate,
              igstRate: service.igstRate || igstRate,
              SACCode: service.SACCode || '',
              selectSAC: service.SACCode || ''
            })) || [],
            totalValue: invoice.totalValue || 0,
            cgstAmount: invoice.totalCGSTAmount || 0,
            sgstAmount: invoice.totalSGSTAmount || 0,
            igstAmount: invoice.totalIGSTAmount || 0,
            totalTaxAmount: invoice.totalTaxAmount || 0,
            totalAmount: invoice.totalAmountWithTax || 0,
            subtotal: invoice.totalValue || 0,
            cgstRate: cgstRate,
            sgstRate: sgstRate,
            igstRate: igstRate,
            notes: invoice.remarks || '',
            terms: Array.isArray(invoice.paymentTerms) 
              ? invoice.paymentTerms 
              : (invoice.paymentTerms 
                  ? (typeof invoice.paymentTerms === 'string' && invoice.paymentTerms.includes('\n')
                      ? invoice.paymentTerms.split('\n').filter(t => t.trim())
                      : [invoice.paymentTerms].filter(t => t.trim()))
                  : []),
            status: invoice.status || 'Draft'
          });
          
          const customerGstin = invoice.customerGSTIN || (invoice.customer?.GSTIN || '');
          if (customerGstin) {
            setCustomerGSTIN(customerGstin);
          }
          
          const customerId = invoice.customer?._id || invoice.customer;
          if (customerId) {
            loadCustomerProjects(customerId);
            setShowServiceTable(true);
          }
          
          if (invoice.serviceDetails && invoice.serviceDetails.length > 0) {
            setServiceData(invoice.serviceDetails.map(service => ({
              _id: service.service?._id || service.service,
              id: service.service?._id || service.service,
              serviceId: service.service?._id || service.service,
              serviceCode: service.serviceCode || '',
              serviceDescription: service.serviceDescription || '',
              SACCode: service.SACCode || '',
              selectSAC: service.SACCode || '',
              gstRate: service.gstRate || 0,
              cgstRate: service.cgstRate || 9,
              sgstRate: service.sgstRate || 9,
              igstRate: service.igstRate || 18,
              quantity: service.qty || 0,
              unitRate: service.unitRate || 0,
              lineValue: service.lineValue || 0
            })));
          }
        }
      }
    } catch (error) {
      console.error('Error loading proforma invoice:', error);
      toast.error('Failed to load proforma invoice');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const calculateTotals = (items) => {
    const subtotal = items.reduce((sum, item) => sum + (item.taxableAmount || 0), 0);
    const isInterState = formData.customerState !== 'NY';
    const cgstAmount = isInterState ? 0 : (subtotal * formData.cgstRate) / 100;
    const sgstAmount = isInterState ? 0 : (subtotal * formData.sgstRate) / 100;
    const igstAmount = isInterState ? (subtotal * formData.igstRate) / 100 : 0;
    const totalTaxAmount = cgstAmount + sgstAmount + igstAmount;
    const totalAmount = subtotal + totalTaxAmount;

    setFormData(prev => ({
      ...prev,
      subtotal,
      cgstAmount,
      sgstAmount,
      igstAmount,
      totalTaxAmount,
      totalAmount
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      let paymentTermsDescription = '';
      if (formData.customerPaymentTerms) {
        const selectedTerm = paymentTermsOptions.find(
          term => (term._id || term.id) === formData.customerPaymentTerms
        );
        paymentTermsDescription = selectedTerm ? selectedTerm.description : '';
      }

      const submissionData = {
        proformaInvoiceDate: formData.proformaInvoiceDate,
        validUntil: formData.validUntil || getDefaultValidUntil(formData.proformaInvoiceDate),
        customerCategory: formData.customerCategory,
        customerName: formData.customerName,
        customer: formData.customerId,
        customerCode: formData.customerCode,
        customerGSTIN: formData.customerGSTIN,
        customerPaymentTerms: paymentTermsDescription || formData.customerPaymentTerms,
        PONo: formData.poNumber,
        PODate: formData.poDate || null,
        projectName: formData.projectName || '',
        project: formData.projectId || null,
        referenceModel: formData.referenceModel || 'Customer',
        currency: formData.currency || 'INR',
        serviceDetails: formData.items.map(item => ({
          service: item.serviceId || item._id || item.id,
          serviceCode: item.serviceCode || item.itemCode || '',
          serviceDescription: item.serviceDescription || item.itemName || item.description || '',
          SACCode: item.SACCode || item.selectSAC || '',
          gstRate: item.gstRate || 0,
          igstRate: item.igstRate || formData.igstRate || 18,
          sgstRate: item.sgstRate || formData.sgstRate || 9,
          cgstRate: item.cgstRate || formData.cgstRate || 9,
          utgstRate: item.utgstRate || 0,
          utgstAmount: item.utgstAmount || 0,
          additionalInfo: item.additionalInfo || '',
          qty: item.quantity || item.qty || 0,
          unitRate: item.unitRate || item.rate || 0,
          lineValue: item.lineValue || (item.quantity * item.rate) || 0
        })).filter(item => item.service && item.SACCode && item.qty > 0 && item.unitRate > 0),
        paymentTerms: Array.isArray(formData.terms) 
          ? formData.terms.join('\n') 
          : (formData.terms || 'Within 30 Days'),
        remarks: formData.remark,
        isLUTBond: formData.isLUTBond || false
      };

      let response;
      if (isEdit) {
        response = await proformaInvoiceService.updateProformaInvoice(id, submissionData);
      } else {
        submissionData.status = 'Draft';
        response = await proformaInvoiceService.createProformaInvoice(submissionData);
      }

      if (response.success) {
        toast.success(isEdit ? 'Proforma invoice updated successfully' : 'Proforma invoice created successfully');
        navigate('/sales/proforma-invoice');
      } else {
        toast.error(response.message || 'Failed to save proforma invoice');
      }
    } catch (error) {
      console.error('Error saving proforma invoice:', error);
      toast.error('Error saving proforma invoice');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/sales/proforma-invoice');
  };

  const handleApprove = () => {
    if (!id || id === 'new') {
      toast.error('Please save the invoice first before approving');
      return;
    }

    if (formData.status !== 'Draft') {
      toast.warning(`Invoice status is already ${formData.status}. Only Draft invoices can be approved.`);
      return;
    }

    setShowApproveModal(true);
  };

  const confirmApprove = async () => {
    setShowApproveModal(false);
    setLoading(true);
    try {
      const response = await proformaInvoiceService.updateProformaInvoice(id, {
        status: 'Approved'
      });

      if (response.success) {
        toast.success('Proforma invoice approved successfully');
        navigate('/sales/proforma-invoice');
      } else {
        toast.error(response.message || 'Failed to approve proforma invoice');
      }
    } catch (error) {
      console.error('Error approving proforma invoice:', error);
      toast.error('Failed to approve proforma invoice');
    } finally {
      setLoading(false);
    }
  };

  const handleConvert = () => {
    if (!id || id === 'new') {
      toast.error('Please save the invoice first before converting');
      return;
    }

    if (formData.status !== 'Approved') {
      toast.warning('Only approved proforma invoices can be converted to tax invoice');
      return;
    }

    setShowConvertModal(true);
  };

  const confirmConvert = async () => {
    setShowConvertModal(false);
    setLoading(true);
    try {
      const response = await proformaInvoiceService.convertToTaxInvoice(id);

      if (response.success) {
        toast.success('Proforma invoice converted to tax invoice successfully');
        navigate('/sales/tax-invoice');
      } else {
        toast.error(response.message || 'Failed to convert proforma invoice');
      }
    } catch (error) {
      console.error('Error converting proforma invoice:', error);
      toast.error('Failed to convert proforma invoice');
    } finally {
      setLoading(false);
    }
  };

  const handleCustomerSearch = () => {
    setShowCustomerModal(true);
  };

  const handleCustomerSelect = (selectedCustomer) => {
    // Find matching payment terms from options
    let defaultPaymentTerms = '';
    if (selectedCustomer.customerPaymentTerms && paymentTermsOptions.length > 0) {
      const customerPaymentTermsStr = selectedCustomer.customerPaymentTerms.toString().trim();
      
      // Try to match by description first (exact match)
      let matchedTerm = paymentTermsOptions.find(
        term => term.description && term.description.toString().trim() === customerPaymentTermsStr
      );
      
      // If no exact match, try partial match on description
      if (!matchedTerm) {
        matchedTerm = paymentTermsOptions.find(
          term => term.description && term.description.toString().trim().includes(customerPaymentTermsStr)
        );
      }
      
      // If still no match, try to match by paymentTermsCode
      if (!matchedTerm) {
        matchedTerm = paymentTermsOptions.find(
          term => term.paymentTermsCode && term.paymentTermsCode.toString().trim() === customerPaymentTermsStr
        );
      }
      
      if (matchedTerm) {
        defaultPaymentTerms = matchedTerm._id || matchedTerm.id;
      }
    }

    // Determine customer category - check both categoryType and customerCategory fields
    // ProformaInvoice requires: 'Domestic', 'Export', 'SEZ', 'Deemed Export'
    // Customer model has: categoryType (enum: 'Domestic', 'Export') and customerCategory (string)
    let customerCategory = 'Domestic'; // Default value
    
    if (selectedCustomer.categoryType) {
      // categoryType is enum: 'Domestic' or 'Export'
      customerCategory = selectedCustomer.categoryType;
    } else if (selectedCustomer.customerCategory) {
      // customerCategory is a string field - map it to valid ProformaInvoice values
      const cat = selectedCustomer.customerCategory.toString().trim();
      if (['Domestic', 'Export', 'SEZ', 'Deemed Export'].includes(cat)) {
        customerCategory = cat;
      } else if (cat.toLowerCase() === 'export' || cat.toLowerCase() === 'international') {
        customerCategory = 'Export';
      } else {
        customerCategory = 'Domestic'; // Default for any other value
      }
    }

    const billingAddr = selectedCustomer.customerBillingAddress?.[0] || {};

    setFormData(prev => ({
      ...prev,
      customerId: selectedCustomer._id || selectedCustomer.id,
      customerName: selectedCustomer.customerName,
      customerCode: selectedCustomer.customerCode, // Keep for backend submission
      customerGSTIN: selectedCustomer.GSTIN || '',
      customerState: billingAddr.state || '',
      billingState: billingAddr.state || '',
      placeOfSupply: billingAddr.state || '',
      customerPaymentTerms: defaultPaymentTerms,
      customerCategory: customerCategory, // Set customer category from customer data
      currency: selectedCustomer.customerCurrency || '', // Populate currency from customer
      projectId: '', // Reset project when customer changes
      projectName: '' // Reset project name when customer changes
    }));
    setCustomerGSTIN(selectedCustomer.GSTIN || '');
    setShowCustomerModal(false);
    setShowServiceTable(true); // Show service table when customer is selected
    
    // Load projects for the selected customer
    loadCustomerProjects(selectedCustomer._id || selectedCustomer.id);
  };

  // Terms & Conditions Management
  const addTerm = () => {
    if (newTerm.trim()) {
      setFormData(prev => ({
        ...prev,
        terms: [...(prev.terms || []), newTerm.trim()]
      }));
      setNewTerm('');
    }
  };

  const removeTerm = (index) => {
    setFormData(prev => ({
      ...prev,
      terms: prev.terms.filter((_, i) => i !== index)
    }));
  };

  const updateTerm = (index, value) => {
    setFormData(prev => ({
      ...prev,
      terms: prev.terms.map((term, i) => i === index ? value : term)
    }));
  };

  const handleTermKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      addTerm();
    }
  };

  const loadCustomerProjects = async (customerId) => {
    if (!customerId) {
      setProjectOptions([]);
      return;
    }

    try {
      setLoadingProjects(true);
      const response = await projectService.getAllProjects({
        customer: customerId,
        status: 'Active',
        limit: 1000,
        page: 1
      });

      if (response && response.success) {
        const projectsList = response.projects || response.data || [];
        const projects = (Array.isArray(projectsList) ? projectsList : []).map(project => ({
          _id: project._id,
          id: project._id,
          projectName: project.projectName,
          projectNumber: project.projectNumber,
          projectDescription: project.projectDescription || ''
        }));
        setProjectOptions(projects);
      } else {
        setProjectOptions([]);
      }
    } catch (error) {
      console.error('Error loading customer projects:', error);
      setProjectOptions([]);
    } finally {
      setLoadingProjects(false);
    }
  };

  const handleProjectSelect = (projectId) => {
    const selectedProject = projectOptions.find(p => (p._id || p.id) === projectId);
    setFormData(prev => ({
      ...prev,
      projectId: projectId || '',
      projectName: selectedProject ? selectedProject.projectName : ''
    }));
  };

  const handleServiceDataChange = (updatedServiceData) => {
    setServiceData(updatedServiceData);
    
    const totalValue = updatedServiceData.reduce((sum, service) => {
      const qty = parseFloat(service.quantity) || 0;
      const rate = parseFloat(service.unitRate) || 0;
      const lineValue = parseFloat(service.lineValue) || (qty * rate);
      return sum + lineValue;
    }, 0);
    
    const transformedItems = updatedServiceData
      .filter(service => {
        const qty = parseFloat(service.quantity) || 0;
        const rate = parseFloat(service.unitRate) || 0;
        return qty > 0 && rate > 0;
      })
      .map(service => ({
        id: service._id || service.id || service.serviceId,
        serviceId: service._id || service.id || service.serviceId,
        itemCode: service.serviceCode || '',
        itemName: service.serviceDescription || '',
        serviceDescription: service.serviceDescription || '',
        description: service.serviceDescription || '',
        SACCode: service.selectSAC || service.SACCode || '',
        selectSAC: service.selectSAC || service.SACCode || '',
        gstRate: service.gstRate || 0,
        igstRate: service.igstRate || formData.igstRate || 18,
        sgstRate: service.sgstRate || formData.sgstRate || 9,
        cgstRate: service.cgstRate || formData.cgstRate || 9,
        quantity: parseFloat(service.quantity) || 0,
        qty: parseFloat(service.quantity) || 0,
        unit: 'Nos',
        rate: parseFloat(service.unitRate) || 0,
        unitRate: parseFloat(service.unitRate) || 0,
        amount: parseFloat(service.lineValue) || (parseFloat(service.quantity) * parseFloat(service.unitRate)) || 0,
        lineValue: parseFloat(service.lineValue) || (parseFloat(service.quantity) * parseFloat(service.unitRate)) || 0,
        taxableAmount: parseFloat(service.lineValue) || (parseFloat(service.quantity) * parseFloat(service.unitRate)) || 0,
        discount: 0,
        additionalInfo: service.additionalInfo || ''
      }));
    
    setFormData(prev => ({
      ...prev,
      items: transformedItems,
      totalValue: Math.round(totalValue * 100) / 100
    }));

    calculateGST(totalValue);
    
    if (serviceTableRef.current) {
      setCanCreateInvoice(serviceTableRef.current.canCreateInvoice || false);
    }
  };

  const calculateGST = (subtotal) => {
    const result = calculateInvoiceGST({
      subtotal,
      supplierGSTIN: companyGSTIN,
      customerGSTIN: customerGSTIN || formData.customerGSTIN,
      customerCategory: formData.customerCategory || 'Domestic',
      placeOfSupply: formData.placeOfSupply || formData.billingState || formData.customerState,
      supplierState: companyState,
      isLUTBond: formData.isLUTBond === true,
      gstRate: formData.igstRate || 18,
      cgstRate: formData.cgstRate || 9,
      sgstRate: formData.sgstRate || 9,
      igstRate: formData.igstRate || 18,
    });

    setFormData(prev => ({
      ...prev,
      subtotal: result.subtotal,
      cgstAmount: result.cgstAmount,
      sgstAmount: result.sgstAmount,
      utgstAmount: result.utgstAmount || 0,
      igstAmount: result.igstAmount,
      totalTaxAmount: result.totalTaxAmount,
      totalAmountBeforeRoundOff: result.totalAmountBeforeRoundOff,
      roundOffAmount: result.roundOffAmount,
      totalAmount: result.totalAmount,
      gstType: result.gstType,
    }));
  };

  const breadcrumbItems = [
    {
      label: 'Sales',
      path: '/sales',
      onClick: () => navigate('/sales')
    },
    {
      label: 'Transactions',
      path: '/sales?tab=transactions',
      onClick: () => navigate('/sales?tab=transactions')
    },
    {
      label: 'Proforma Invoice',
      path: '/sales/proforma-invoice',
      onClick: () => navigate('/sales/proforma-invoice')
    },
    {
      label: isEdit ? 'Edit Invoice' : isView ? 'View Invoice' : isApprove ? 'Approve Invoice' : 'Create Invoice',
      path: window.location.pathname,
      isCurrent: true
    }
  ];

  const helpContent = (
    <>
      <h3>What is Proforma Invoice?</h3>
      <p>Proforma Invoice is a <strong>preliminary invoice</strong> for approval before issuing a tax invoice. It is not a GST invoice; no input credit can be claimed. Use it to share expected charges and terms with the customer.</p>
      <h3>Tabs</h3>
      <p><strong>Invoice Details</strong> – Proforma number, date, valid until, PO details, customer, billing address, and line items (add via service selection).</p>
      <p><strong>GST &amp; Totals</strong> – GST type (IGST/CGST+SGST), rates, amounts, subtotal, total. Optional LUT/Bond and project reference.</p>
      <p><strong>Notes &amp; Terms</strong> – Notes, terms and conditions, status. After approval you can <strong>Convert to Tax Invoice</strong>.</p>
      <h3>How to use</h3>
      <p>Select a <strong>customer</strong>, add line items, set validity. Save as Draft, then submit for approval. Approved proforma can be converted to a Tax Invoice.</p>
    </>
  );

  return (
    <div className="sales-dashboard">
      <div className="breadcrumb-section">
        <Breadcrumb
          items={breadcrumbItems}
          rightSlot={
            <button type="button" className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1" onClick={() => setShowHelp(true)} title="Help" aria-label="Open help">
              <HelpCircle size={18} />
              <span className="d-none d-sm-inline">Help</span>
            </button>
          }
        />
      </div>

      <div className="page-header">
        <div className="page-title">
          <h1>{isApprove ? 'Approve Proforma Invoice' : isEdit ? 'Edit Proforma Invoice' : isView ? 'View Proforma Invoice' : 'Create Proforma Invoice'}</h1>
          <p>{isApprove ? 'Review and approve proforma invoice' : isEdit ? 'Update proforma invoice details' : isView ? 'View proforma invoice information' : 'Generate new proforma invoice'}</p>
        </div>
        {(!isEdit || isApprove) && (
          <div className="page-actions" style={{ display: 'flex', gap: '12px' }}>
            {!isView && !isApprove && (
              <button 
                type="submit" 
                form="proforma-invoice-form"
                className="btn-primary" 
                disabled={loading}
              >
                <Save size={18} />
                {loading ? 'Saving...' : (isEdit ? 'Update Invoice' : 'Create Invoice')}
              </button>
            )}
            {(isApprove || (!isView && isEdit && formData.status === 'Draft')) && (
              <button 
                type="button"
                className="btn-primary" 
                onClick={handleApprove}
                disabled={loading || (isApprove && formData.status !== 'Draft')}
                style={{ backgroundColor: '#10b981', borderColor: '#10b981' }}
              >
                <CheckCircle size={18} />
                {loading ? 'Approving...' : 'Approve Invoice'}
              </button>
            )}
            {!isView && !isApprove && formData.status === 'Approved' && (
              <button 
                type="button"
                className="btn-primary" 
                onClick={handleConvert}
                disabled={loading}
                style={{ backgroundColor: '#3b82f6', borderColor: '#3b82f6' }}
              >
                <RefreshCw size={18} />
                Convert to Tax Invoice
              </button>
            )}
            <button className="btn-secondary" onClick={handleCancel}>
              <X size={18} />
              Cancel
            </button>
          </div>
        )}
      </div>

      <div className="form-container">
        <div className="sac-form-header">
          <h2>Proforma Invoice Entry</h2>
        </div>
        
        <div className="tab-navigation">
          <button
            className={`tab-button ${activeTab === 'invoiceDetails' ? 'active' : ''}`}
            onClick={() => setActiveTab('invoiceDetails')}
          >
            <FileText size={18} />
            <span>Invoice Details</span>
          </button>
          <button
            className={`tab-button ${activeTab === 'gstDetails' ? 'active' : ''}`}
            onClick={() => setActiveTab('gstDetails')}
          >
            <Calculator size={18} />
            <span>GST Details</span>
          </button>
        </div>
          
        <form id="proforma-invoice-form" className="form-content" onSubmit={handleSubmit}>
          
          {activeTab === 'invoiceDetails' && (
            <div className="tab-content">
              <div className="section-header">
                <h3>Invoice Information</h3>
              </div>
              
              <div className="grid-row-4">
                <div className="form-group">
                  <label htmlFor="proformaInvoiceNumber" className="form-label required">
                    Invoice #
                  </label>
                  <input
                    type="text"
                    id="proformaInvoiceNumber"
                    name="proformaInvoiceNumber"
                    value={formData.proformaInvoiceNumber || 'Auto-generated on save'}
                    readOnly
                    className="form-input"
                    placeholder="Auto-generated"
                    style={{ backgroundColor: '#f9fafb', color: 'var(--color-text-secondary, #5B6478)', cursor: 'not-allowed' }}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="proformaInvoiceDate" className="form-label required">
                    Invoice Date
                  </label>
                  <input
                    type="date"
                    id="proformaInvoiceDate"
                    name="proformaInvoiceDate"
                    value={formData.proformaInvoiceDate}
                    onChange={handleInputChange}
                    className="form-input"
                    disabled={isView || isApprove}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="customerCategory" className="form-label required">
                    Customer/Sales Category
                  </label>
                  <select
                    id="customerCategory"
                    name="customerCategory"
                    value={formData.customerCategory}
                    onChange={handleInputChange}
                    className="form-input"
                    disabled={isView || isApprove}
                  >
                    <option value="">Select</option>
                    <option value="Domestic">Domestic</option>
                    <option value="Export">Export</option>
                    <option value="SEZ">SEZ</option>
                    <option value="Deemed Export">Deemed Export</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="customerName" className="form-label required">
                    Customer Name
                  </label>
                  <div className="search-input-wrapper">
                    <input
                      type="text"
                      id="customerName"
                      name="customerName"
                      value={formData.customerName}
                      readOnly
                      className="form-input search-input"
                      placeholder="Select customer"
                      style={{ backgroundColor: '#f9fafb', color: 'var(--color-text-secondary, #5B6478)', cursor: 'not-allowed' }}
                      disabled={isView || isApprove}
                    />
                    <button
                      type="button"
                      className="search-button"
                      title="Search Customer"
                      onClick={handleCustomerSearch}
                      disabled={isView || isApprove}
                    >
                      <Search size={16} />
                    </button>
                  </div>
                </div>
              </div>

              <div className="grid-row-4">
                <div className="form-group">
                  <label htmlFor="projectId" className="form-label">
                    Project Name / CR Name
                  </label>
                  <select
                    id="projectId"
                    name="projectId"
                    value={formData.projectId}
                    onChange={(e) => handleProjectSelect(e.target.value)}
                    className="form-input"
                    disabled={isView || isApprove || !formData.customerId || loadingProjects}
                  >
                    <option value="">Select</option>
                    {loadingProjects ? (
                      <option value="" disabled>Loading projects...</option>
                    ) : projectOptions.length === 0 && formData.customerId ? (
                      <option value="" disabled>No active projects found</option>
                    ) : (
                      projectOptions.map((project) => (
                        <option key={project._id || project.id} value={project._id || project.id}>
                          {project.projectName} {project.projectNumber ? `(${project.projectNumber})` : ''}
                        </option>
                      ))
                    )}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="poDate" className="form-label">
                    Purchase Order Date
                  </label>
                  <input
                    type="date"
                    id="poDate"
                    name="poDate"
                    value={formData.poDate}
                    onChange={handleInputChange}
                    className="form-input"
                    disabled={isView || isApprove}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="poNumber" className="form-label">
                    Purchase Order No.
                  </label>
                  <input
                    type="text"
                    id="poNumber"
                    name="poNumber"
                    value={formData.poNumber}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="Enter PO number"
                    disabled={isView || isApprove}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="customerPaymentTerms" className="form-label required">
                    Payment Terms
                  </label>
                  <select
                    id="customerPaymentTerms"
                    name="customerPaymentTerms"
                    value={formData.customerPaymentTerms}
                    onChange={handleInputChange}
                    className="form-input"
                    disabled={isView || isApprove}
                  >
                    <option value="">Select Payment Terms</option>
                    {paymentTermsOptions.map((term) => (
                      <option key={term._id || term.id} value={term._id || term.id}>
                        {term.paymentTermsCode} - {term.description}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {showServiceTable && (
                <>
                  <div className="section-header" style={{ width: '100%', maxWidth: 'none', marginBottom: '0' }}>
                    <h3>Service Selection</h3>
                    <p>Select services and enter quantities/rates for the selected customer</p>
                  </div>
                  <div style={{ width: '100%', marginTop: '0', marginLeft: '0', marginRight: '0', paddingLeft: '0', paddingRight: '0' }}>
                    <ServiceSelectionTable 
                      ref={serviceTableRef}
                      onServiceDataChange={(updatedServiceData) => {
                        handleServiceDataChange(updatedServiceData)
                        setTimeout(() => {
                          if (serviceTableRef.current) {
                            setCanCreateInvoice(serviceTableRef.current.canCreateInvoice || false)
                          }
                        }, 0)
                      }}
                      initialServiceData={serviceData}
                      isEditMode={isEdit}
                      isViewMode={isView}
                      isApproveMode={isApprove}
                    />
                  </div>
                </>
              )}

            </div>
          )}

          {activeTab === 'gstDetails' && (
            <div className="tab-content">
              <div className="section-header">
                <h3>GST Information</h3>
              </div>

              <div className="grid-row-2">
                <div className="form-group">
                  <label htmlFor="customerGSTIN" className="form-label">
                    GSTIN
                  </label>
                  <input
                    type="text"
                    id="customerGSTIN"
                    name="customerGSTIN"
                    value={formData.customerGSTIN}
                    readOnly
                    className="form-input"
                    placeholder="Enter GSTIN"
                    style={{ backgroundColor: '#f3f4f6', cursor: 'not-allowed' }}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="currency" className="form-label required">
                    Currency
                  </label>
                  <input
                    type="text"
                    id="currency"
                    name="currency"
                    value={formData.currency || ''}
                    readOnly
                    className="form-input"
                    placeholder={formData.currency ? '' : 'Select customer to set currency'}
                    style={{ backgroundColor: '#f3f4f6', cursor: 'not-allowed' }}
                  />
                </div>
              </div>

              <div className="section-header">
                <h3>GST Calculation</h3>
              </div>

              <div className="grid-row-3">
                <div className="form-group">
                  <label htmlFor="cgstRate" className="form-label">
                    CGST Rate (%)
                  </label>
                  <input
                    type="number"
                    id="cgstRate"
                    name="cgstRate"
                    value={formData.cgstRate}
                    readOnly
                    className="form-input"
                    min="0"
                    max="100"
                    step="0.01"
                    style={{ backgroundColor: '#f3f4f6', cursor: 'not-allowed' }}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="sgstRate" className="form-label">
                    SGST Rate (%)
                  </label>
                  <input
                    type="number"
                    id="sgstRate"
                    name="sgstRate"
                    value={formData.sgstRate}
                    readOnly
                    className="form-input"
                    min="0"
                    max="100"
                    step="0.01"
                    style={{ backgroundColor: '#f3f4f6', cursor: 'not-allowed' }}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="igstRate" className="form-label">
                    IGST Rate (%)
                  </label>
                  <input
                    type="number"
                    id="igstRate"
                    name="igstRate"
                    value={formData.igstRate}
                    readOnly
                    className="form-input"
                    min="0"
                    max="100"
                    step="0.01"
                    style={{ backgroundColor: '#f3f4f6', cursor: 'not-allowed' }}
                  />
                </div>
              </div>

              <div className="section-header">
                <h3>Tax Summary</h3>
              </div>

              <div className="grid-row-2">
                <div className="form-group">
                  <label className="form-label">Subtotal</label>
                  <div className="form-input readonly-input">
                    ₹ {formData.subtotal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Total Tax Amount</label>
                  <div className="form-input readonly-input">
                    ₹ {formData.totalTaxAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                  </div>
                </div>
              </div>

              <div className="grid-row-3">
                <div className="form-group">
                  <label className="form-label">CGST Amount</label>
                  <div className="form-input readonly-input">
                    ₹ {formData.cgstAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">SGST Amount</label>
                  <div className="form-input readonly-input">
                    ₹ {formData.sgstAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">IGST Amount</label>
                  <div className="form-input readonly-input">
                    ₹ {formData.igstAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                  </div>
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Total Amount</label>
                <div className="form-input readonly-input" style={{ 
                  fontSize: '18px', 
                  fontWeight: 'bold', 
                  backgroundColor: '#F0EBF7', 
                  borderColor: '#6A4CA2',
                  color: '#51418F'
                }}>
                  ₹ {formData.totalAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                </div>
              </div>

              <div className="section-header">
                <h3>Additional Information</h3>
              </div>

              <div className="grid-row-2">
                <div className="form-group">
                  <label htmlFor="notes" className="form-label">
                    Notes
                  </label>
                  <textarea
                    id="notes"
                    name="notes"
                    value={formData.notes}
                    onChange={handleInputChange}
                    className="form-textarea"
                    placeholder="Additional notes or comments"
                    rows={3}
                    disabled={isView || isApprove}
                  />
                </div>

                  <div className="form-group">
                    <label htmlFor="terms" className="form-label">
                      Terms & Conditions
                    </label>
                    <div
                      style={{
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        padding: '16px',
                        backgroundColor: isView || isApprove ? '#f9fafb' : '#fff',
                        minHeight: '200px'
                      }}
                    >
                      {/* Existing Terms List */}
                      {formData.terms && formData.terms.length > 0 && (
                        <div style={{ marginBottom: '16px' }}>
                          {formData.terms.map((term, index) => (
                            <div 
                              key={index} 
                              style={{ 
                                display: 'flex', 
                                gap: '8px', 
                                marginBottom: '8px',
                                padding: '10px 12px',
                                background: '#f8f9fa',
                                borderRadius: '6px',
                                alignItems: 'flex-start',
                                border: '1px solid #e5e7eb'
                              }}
                            >
                              <span style={{ 
                                color: '#222448', 
                                fontWeight: '600', 
                                marginRight: '8px',
                                marginTop: '2px',
                                fontSize: '14px'
                              }}>•</span>
                              {isView || isApprove ? (
                                <div style={{ flex: 1, color: '#374151', fontSize: '14px', lineHeight: '1.5' }}>
                                  {term}
                                </div>
                              ) : (
                                <>
                                  <input
                                    type="text"
                                    value={term}
                                    onChange={(e) => updateTerm(index, e.target.value)}
                                    className="form-input"
                                    style={{ 
                                      flex: 1, 
                                      marginBottom: 0,
                                      padding: '6px 10px',
                                      fontSize: '14px'
                                    }}
                                    placeholder="Enter term or condition"
                                  />
                                  <button
                                    type="button"
                                    onClick={() => removeTerm(index)}
                                    style={{
                                      padding: '6px 10px',
                                      border: 'none',
                                      background: '#ef4444',
                                      color: '#fff',
                                      borderRadius: '4px',
                                      cursor: 'pointer',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      minWidth: '36px',
                                      height: '36px'
                                    }}
                                    title="Remove this term"
                                  >
                                    <Trash2 size={16} />
                                  </button>
                                </>
                              )}
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Add New Term */}
                      {!isView && !isApprove && (
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                          <span style={{ 
                            color: '#222448', 
                            fontWeight: '600', 
                            marginTop: '10px',
                            fontSize: '14px'
                          }}>•</span>
                          <input
                            type="text"
                            value={newTerm}
                            onChange={(e) => setNewTerm(e.target.value)}
                            onKeyPress={handleTermKeyPress}
                            className="form-input"
                            placeholder="Enter a new term or condition and press Enter or click Add"
                            style={{ 
                              flex: 1, 
                              marginBottom: 0,
                              padding: '8px 12px',
                              fontSize: '14px'
                            }}
                          />
                          <button
                            type="button"
                            onClick={addTerm}
                            disabled={!newTerm.trim()}
                            style={{
                              padding: '8px 16px',
                              border: 'none',
                              background: newTerm.trim() ? '#222448' : '#9ca3af',
                              color: '#fff',
                              borderRadius: '6px',
                              cursor: newTerm.trim() ? 'pointer' : 'not-allowed',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px',
                              fontWeight: '500',
                              fontSize: '14px',
                              height: '38px'
                            }}
                            title="Add this term"
                          >
                            <Plus size={16} />
                            Add
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
              </div>
            </div>
          )}

        </form>

        <div className="form-actions" style={{ 
          display: 'flex', 
          gap: '20px', 
          alignItems: 'end',
          marginTop: '20px',
          padding: '20px',
          backgroundColor: '#f8fafc',
          borderTop: '1px solid #e2e8f0',
          justifyContent: 'space-between'
        }}>
          <div style={{ display: 'flex', gap: '20px', alignItems: 'end' }}>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label" style={{ color: '#222448', fontWeight: '600' }}>
                Total Value:
              </label>
              <input
                type="text"
                value={formData.totalValue ? formData.totalValue.toFixed(2) : '0.00'}
                readOnly
                className="form-input"
                style={{ 
                  backgroundColor: '#f9fafb', 
                  color: 'var(--color-text-secondary, #5B6478)', 
                  cursor: 'not-allowed',
                  textAlign: 'right',
                  fontWeight: '600',
                  width: '150px'
                }}
              />
            </div>
            
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label" style={{ color: '#222448', fontWeight: '600' }}>
                Remark:
              </label>
              <input
                type="text"
                name="remark"
                value={formData.remark || ''}
                onChange={handleInputChange}
                className="form-input"
                placeholder="Enter remarks..."
                disabled={isView || isApprove}
                style={{ width: '250px' }}
              />
            </div>
          </div>

          {!isApprove && (
            <div style={{ display: 'flex', gap: '12px' }}>
              <button 
                type="button" 
                className="btn-secondary" 
                onClick={() => {
                  window.location.reload();
                }}
                disabled={isView}
              >
                Reset
              </button>
              
              <button 
                type="button" 
                className="btn-secondary" 
                onClick={() => {
                  if (serviceTableRef.current && serviceTableRef.current.handleEsc) {
                    serviceTableRef.current.handleEsc()
                    setTimeout(() => {
                      setCanCreateInvoice(false)
                    }, 100)
                  }
                }}
                disabled={isView}
                title={isEdit ? "Load all available services from service master while keeping current invoice services. Use this to add more services to your invoice." : "Load all available services from service master while keeping services you've already filled. Use this to add more services to your invoice."}
              >
                Add More Services
              </button>
              
              <button 
                type="button" 
                className="btn-secondary"
                onClick={() => {
                  if (serviceTableRef.current && serviceTableRef.current.handlePreview) {
                    serviceTableRef.current.handlePreview()
                    setTimeout(() => {
                      if (serviceTableRef.current) {
                        setCanCreateInvoice(serviceTableRef.current.canCreateInvoice || false)
                      }
                    }, 100)
                  }
                }}
                disabled={isView}
                title="Filter the table to show only services with quantity and rate entered. This helps you review the services that will be included in your invoice before creating it."
              >
                Preview Selected
              </button>
              
              {!isView && (
                <button 
                  type="button" 
                  className="btn-primary" 
                  onClick={handleSubmit}
                  disabled={loading || !canCreateInvoice}
                  title={!canCreateInvoice ? 'Please click Preview button and ensure services have quantity and rate entered' : ''}
                >
                  {loading ? 'Creating...' : (isEdit ? 'Update Invoice' : 'Create Invoice')}
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      <CustomerSelectionModal
        isOpen={showCustomerModal}
        onClose={() => setShowCustomerModal(false)}
        onSelectCustomer={handleCustomerSelect}
      />

      <ConfirmationModal
        isOpen={showApproveModal}
        onClose={() => setShowApproveModal(false)}
        onConfirm={confirmApprove}
        title="Approve Proforma Invoice"
        message={`Are you sure you want to approve invoice ${formData.proformaInvoiceNumber}? This action cannot be undone.`}
        confirmText="Approve Invoice"
        cancelText="Cancel"
        type="success"
      />

      <ConfirmationModal
        isOpen={showConvertModal}
        onClose={() => setShowConvertModal(false)}
        onConfirm={confirmConvert}
        title="Convert to Tax Invoice"
        message={`Are you sure you want to convert proforma invoice ${formData.proformaInvoiceNumber} to a tax invoice? This action cannot be undone.`}
        confirmText="Convert to Tax Invoice"
        cancelText="Cancel"
        type="info"
      />

      <HelpModal isOpen={showHelp} onClose={() => setShowHelp(false)} title="Proforma Invoice – Help">
        {helpContent}
      </HelpModal>
    </div>
  );
};

export default ProformaInvoiceForm;

